/**
 * Helper unificado para mostrar costos en el Backoffice (Fase 34).
 *
 * Decisión del producto:
 *   - Las vistas operativas del BO (lista de sesiones, detalle, waterfall,
 *     charts, compare, turns table) muestran **raw provider cost (sin
 *     markup)** por default — el BO se usa para análisis interno, no para
 *     simular UX del user.
 *   - Reportes especiales (P&L, margin, revenue) que se construyen después
 *     consumen `costNano` (con markup) + `providerCostNano` + `markupBps` para
 *     mostrar las 3 columnas explícitas.
 *
 * Por qué el fallback:
 *   - Spans persistidos antes de la migration 0061 fueron backfilleados al
 *     bps default (2000). Cualquier span post-migration 0061 que no usó
 *     `setCostBreakdown` tiene `provider_cost_nano = NULL`. En ese caso este
 *     helper cae a `cost_nano` (que SÍ existe) — el operador ve algo en
 *     vez de "—".
 *   - El fallback intencional asume que la divergencia será chica
 *     (paths que no propagan breakdown completo aún). Los reportes
 *     formales no usan este helper — usan los campos directos.
 */

/**
 * Devuelve el cost preferido para vistas operativas:
 *   raw cost si está set, sino cost total con markup como fallback visual.
 *
 * Acepta strings (formato JSON serializado de bigint) o bigint directo.
 */
export function preferRawCost(
  providerCostNano: string | bigint | null | undefined,
  costNano: string | bigint | null | undefined,
): string | null {
  if (providerCostNano !== null && providerCostNano !== undefined) {
    // '0' es raw legítimo (call gratis o sin pricing) — no fallback al total.
    return typeof providerCostNano === 'bigint' ? providerCostNano.toString() : providerCostNano;
  }
  if (costNano !== null && costNano !== undefined) {
    return typeof costNano === 'bigint' ? costNano.toString() : costNano;
  }
  return null;
}

/** Versión bigint del helper para sums internas en charts. */
export function preferRawCostBigInt(
  providerCostNano: string | bigint | null | undefined,
  costNano: string | bigint | null | undefined,
): bigint {
  if (providerCostNano !== null && providerCostNano !== undefined) {
    return typeof providerCostNano === 'bigint' ? providerCostNano : BigInt(providerCostNano);
  }
  if (costNano !== null && costNano !== undefined) {
    return typeof costNano === 'bigint' ? costNano : BigInt(costNano);
  }
  return 0n;
}
